import React, { useContext, useMemo, useRef, useState } from 'react';

import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import ActivityApi from "../../../api/instance/ActivityApi";

// Context
import { TimeRangeContext } from "../../../context/TimeRangeContext";
import { buildApiQuery } from "../../../helpers/timeRangeUtils";
import { useLicences } from "../../../context/LicenceContext";

// Types.
import ChartPlotLine from '../../../types/ChartPlotLine';
import ChartSeries from '../../../types/ChartSeries';
import Time from '../../../types/instance/Time';
import Wait from '../../../types/instance/Wait';
import InstanceTarget from "../../../types/instance/InstanceTarget";
import ClientsDataTable from "../../../types/instance/tables/ClientsDataTable";
import ProgramsDataTable from "../../../types/instance/tables/ProgramsDataTable";
import UsersDataTable from "../../../types/instance/tables/UsersDataTable";
import SessionsDataTable from "../../../types/instance/tables/SessionsDataTable";
import DatabasesDataTable from "../../../types/instance/tables/DatabasesDataTable";

// Constants.
import { CHART_COLOURS_STATEMENTS, STATEMENT_CHART_DATA_OPTION, CHART_DATA_TYPE, DATA_LOADED, DATA_LOADING } from '../../Constants';

// Helpers
import { chartOptionsGranularity } from "../../../helpers/chartOptionsIncreasedGranularity";
import { getConvertedTimeToUTC, highchartsCredits } from "../../../helpers/utils";
import { getAPIString, StatementTypeInfo } from "../../../views/instances/tabs/utils";
import Helper from '../../../helpers/Helper';
import ConditionalRender from "../../../helpers/ConditionalRender";


function StatementTimeChart(props: {
    dataOption: STATEMENT_CHART_DATA_OPTION,
    instance: InstanceTarget,
    batchId?: string,
    isGroup: boolean,
    batchStatementId?: string,
    statementId?: string,
    filtersParameters: string,
    clients: ClientsDataTable[],
    programs: ProgramsDataTable[],
    users: UsersDataTable[],
    sessions: SessionsDataTable[],
    databases: DatabasesDataTable[],
    plotLines: ChartPlotLine[],
    waits: Wait[],
    statementType?: StatementTypeInfo
}) {
    const timeRangeContext = useContext(TimeRangeContext)
    const isAutorefresh = timeRangeContext.timeRange.autorefresh
    const { licences } = useLicences();
    const chartRef = useRef(null);

    const [loading, setLoading] = useState<number>(DATA_LOADING);

    const [timeSeries, setTimeSeries] = useState<ChartSeries[]>([]);
    const [waitSeries, setWaitSeries] = useState<number[][]>([]);
    const [clientsSeries, setClientsSeries] = useState<number[][]>([]);
    // const [batchSeries, setBatchSeries] = useState<number[][]>([]);
    const [programsSeries, setProgramsSeries] = useState<number[][]>([]);
    const [usersSeries, setUsersSeries] = useState<number[][]>([]);
    const [sessionsSeries, setSessionsSeries] = useState<number[][]>([]);
    const [databasesSeries, setDatabasesSeries] = useState<number[][]>([]);

    const timePeak = useMemo(() => {
        // @ts-ignore
        return Helper.getPeakValue(timeSeries.map(i => i[1]))
    }, [timeSeries])

    const apiString = getAPIString(props.statementId || props.batchId, props.batchStatementId, props.statementType)

    void useMemo(async () => {
        setLoading(DATA_LOADING);
        // Get time for the period.
        ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instance.id, `${apiString}${props.filtersParameters}`)
            .then((response: { data: Time[]; }) => {
                // @ts-ignore
                setTimeSeries(response.data.map(item => {
                    return [getConvertedTimeToUTC(item), item.waittime]
                }));
                setLoading(DATA_LOADED);
            })
            .catch((error: any) => {
                console.error('Failed to retrieve activity time.', error);
                setLoading(DATA_LOADED); // Set to loaded even on error to prevent hanging
            })
    }, [props.batchId, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.instance, props.statementId, props.filtersParameters])

    void useMemo(async () => {
        setLoading(DATA_LOADING);

        const waitsPromises = []
        let waitSeries: any[] = []

        try {
            // Get the top individual waits for the period.
            for (let index = 0; index < props.waits.slice(0, 10).length; index++) {
                let wait: Wait = props.waits[index];
                waitsPromises.push(ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instance.id, `&waitevent=${encodeURIComponent(wait.waitevent)}${apiString}${props.filtersParameters}`))
            }

            const responses = await Promise.all(waitsPromises)
            responses.forEach((response: { data: Time[]; }, index: number) => {
                let wait: Wait = props.waits[index];
                let waitData: number[][] = [];

                waitData = waitData.concat(response.data.map(item => {
                    return [getConvertedTimeToUTC(item), item.waittime]
                }));

                // Add the chart series.
                waitSeries.push({
                    name: wait.waitevent,
                    color: wait.color,
                    type: 'column',
                    data: waitData,
                    tooltip: {
                        valueSuffix: 'ms',
                    },
                    zIndex: 1
                });
            })

            setWaitSeries(waitSeries);
            setLoading(DATA_LOADED);
        } catch (error) {
            console.error('Failed to retrieve waits data.', error);
            setLoading(DATA_LOADED); // Still set to loaded to show whatever data we have
        }
    }, [props.batchId, props.statementId, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.instance, props.waits, props.filtersParameters])

    const useFetchChartData = ({
        dataOption,
        dataKey,
        apiParam,
        idKey,
        setSeries,
        props,
        timeRangeContext
    }: any) => {
        void useMemo(async () => {
            // Only fetch if this chart option is currently selected
            if (props.dataOption !== dataOption) return;

            // Only fetch if we have the required data from parent component
            // This prevents fetching when data is still loading in background
            if (!props[dataKey] || props[dataKey].length === 0) {
                // Data not available yet - will be fetched in background
                setSeries([]);
                return;
            }

            setLoading(DATA_LOADING);

            try {
                const promises = props[dataKey].slice(0, 10).map((item: any) => {
                    if (item[idKey] !== null) {
                        return ActivityApi.getTime(buildApiQuery(timeRangeContext.timeRange), props.instance.id, `&${apiParam}=${encodeURIComponent(item[idKey])}${apiString}${props.filtersParameters}`);
                    }
                    return null;
                }).filter(Boolean);

                const responses = await Promise.all(promises);
                const seriesData = responses.map((response, index) => {
                    const item = props[dataKey][index];
                    const data = response.data.map((entry: any) => [getConvertedTimeToUTC(entry), entry.waittime]);
                    return {
                        name: item[idKey] || "-",
                        color: CHART_COLOURS_STATEMENTS[index],
                        type: "column" as const,
                        data,
                        tooltip: { valueSuffix: "ms" },
                        url: `/instances/${props.instanceId}/activity/${dataKey}/${encodeURIComponent(item[idKey])}?${buildApiQuery(timeRangeContext.timeRange)}`,
                        zIndex: index
                    };
                });

                setSeries(seriesData);
                setLoading(DATA_LOADED);
            } catch (error) {
                console.error(`Failed to fetch ${dataKey} chart data:`, error);
                setLoading(DATA_LOADED);
            }
        }, [timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.instanceId, props[dataKey], props.dataOption]);
    };

    // useFetchChartData({
    //     dataOption: STATEMENT_CHART_DATA_OPTION.BATCHES,
    //     dataKey: "batches",
    //     apiParam: props.isGroup ? 'batchgrouphash' : "batchsqlhash",
    //     idKey: props.isGroup ? 'batchgrouphash' : "batchsqlhash",
    //     setSeries: setBatchSeries,
    //     props,
    //     timeRangeContext
    // });
    useFetchChartData({ dataOption: STATEMENT_CHART_DATA_OPTION.CLIENTS, dataKey: "clients", apiParam: "client", idKey: "client", setSeries: setClientsSeries, props, timeRangeContext });
    useFetchChartData({ dataOption: STATEMENT_CHART_DATA_OPTION.PROGRAMS, dataKey: "programs", apiParam: "program", idKey: "program", setSeries: setProgramsSeries, props, timeRangeContext });
    useFetchChartData({ dataOption: STATEMENT_CHART_DATA_OPTION.USERS, dataKey: "users", apiParam: "username", idKey: "username", setSeries: setUsersSeries, props, timeRangeContext });
    useFetchChartData({ dataOption: STATEMENT_CHART_DATA_OPTION.SESSIONS, dataKey: "sessions", apiParam: "session", idKey: "session", setSeries: setSessionsSeries, props, timeRangeContext });
    useFetchChartData({ dataOption: STATEMENT_CHART_DATA_OPTION.DATABASES, dataKey: "databases", apiParam: "database", idKey: "database", setSeries: setDatabasesSeries, props, timeRangeContext });

    const timeChart = useMemo(() => {
        const timeChartOptions = chartOptionsGranularity([
            {
                type: 'column', data: timeSeries, name: 'Time Spent', zIndex: 1, tooltip: { valueSuffix: 'ms', }
            },
        ], timePeak, {
            credits: { enabled: highchartsCredits(licences) },
            legend: true,
            timeRangeContext: timeRangeContext,
            plotLines: props.plotLines,
            tooltip: {
                formatter: function () {
                    return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                },
            },
        })
        return <HighchartsReact useUtcconstructorType={"chart"} highcharts={Highcharts}
            options={timeChartOptions} />;

    }, [timeSeries, timePeak, timeRangeContext.timeRange.from.valueOf(), timeRangeContext.timeRange.to.valueOf(), timeRangeContext.timeRange.interval, props.plotLines, licences]);

    const generateChart = (dataOption: number, chartSeries: number[][]) => {
        if (props.dataOption === dataOption) {
            const chartOptions = chartOptionsGranularity(chartSeries, timePeak, {
                credits: { enabled: highchartsCredits(licences) },
                legend: true,
                timeRangeContext: timeRangeContext,
                plotLines: props.plotLines,
                tooltip: {
                    formatter: function () {
                        return Helper.getChartTooltipsNew(this, CHART_DATA_TYPE.TIME);
                    },
                },
            });

            return <HighchartsReact constructorType={"chart"} highcharts={Highcharts} options={chartOptions} />;
        }

        return null;
    };

    // const batchesChart = useMemo(() => generateChart(STATEMENT_CHART_DATA_OPTION.BATCHES, batchSeries), [
    //     batchSeries, timePeak, props.plotLines, props.dataOption, licences, props.clients
    // ]);

    const waitsChart = useMemo(() => generateChart(STATEMENT_CHART_DATA_OPTION.WAITS, waitSeries), [
        waitSeries, timePeak, props.plotLines, props.dataOption, licences, props.clients
    ]);

    const clientsChart = useMemo(() => generateChart(STATEMENT_CHART_DATA_OPTION.CLIENTS, clientsSeries), [
        clientsSeries, timePeak, props.plotLines, props.dataOption, licences, props.clients
    ]);

    const programsChart = useMemo(() => generateChart(STATEMENT_CHART_DATA_OPTION.PROGRAMS, programsSeries), [
        programsSeries, timePeak, props.plotLines, props.dataOption, licences, props.programs
    ]);

    const usersChart = useMemo(() => generateChart(STATEMENT_CHART_DATA_OPTION.USERS, usersSeries), [
        usersSeries, timePeak, props.plotLines, props.dataOption, licences, props.users
    ]);

    const sessionsChart = useMemo(() => generateChart(STATEMENT_CHART_DATA_OPTION.SESSIONS, sessionsSeries), [
        sessionsSeries, timePeak, props.plotLines, props.dataOption, licences, props.sessions
    ]);

    const databasesChart = useMemo(() => generateChart(STATEMENT_CHART_DATA_OPTION.DATABASES, databasesSeries), [
        databasesSeries, timePeak, props.plotLines, props.dataOption, licences, props.sessions
    ]);

    const chartMap = {
        [STATEMENT_CHART_DATA_OPTION.ACTIVITY]: timeChart,
        [STATEMENT_CHART_DATA_OPTION.WAITS]: waitsChart,
        [STATEMENT_CHART_DATA_OPTION.CLIENTS]: clientsChart,
        [STATEMENT_CHART_DATA_OPTION.PROGRAMS]: programsChart,
        [STATEMENT_CHART_DATA_OPTION.USERS]: usersChart,
        [STATEMENT_CHART_DATA_OPTION.SESSIONS]: sessionsChart,
        [STATEMENT_CHART_DATA_OPTION.DATABASES]: databasesChart,
    };

    // @ts-ignore
    const selectedChart = chartMap[props.dataOption];

    return (
        <React.Fragment>
            <ConditionalRender if={loading === DATA_LOADING && !isAutorefresh}>
                <div className="w-100 text-center time-chart-center text-muted mt-3">
                    <div className="loader spinner chartSpinner"></div>
                    <p>Loading data...</p>
                </div>
            </ConditionalRender>

            <ConditionalRender if={loading === DATA_LOADED || isAutorefresh}>
                <span className="peak">
                    {(timePeak === Infinity || timePeak === -Infinity) ? (
                        <React.Fragment>Peak Time: -</React.Fragment>
                    ) : (
                        <React.Fragment>Peak Time: {Helper.getTimeInEnglish(timePeak)}</React.Fragment>
                    )}
                </span>
                <br />

                <div className={selectedChart ? 'mt-35' : 'd-none'}>
                    <div ref={chartRef}>
                        {selectedChart || (
                            <div className="w-100 text-center text-muted my-3">
                                <p>Chart data is loading in the background...</p>
                            </div>
                        )}
                    </div>
                </div>
            </ConditionalRender>

        </React.Fragment>

    );
}

export default StatementTimeChart;
